/**
 * EVERY CLAIM THIS APP MAKES ABOUT WHAT IT DOES NOT DO, AND THE PROOF FOR EACH.
 *
 * Anthony, 2026-09-17: "Why is the inspection checklist gone."
 *
 * It was not gone. src/pages/inspection.ts is 349 lines, nine sections,
 * four verdicts, a tread depth on tires, linked from every job under Work
 * and live in the bundle. What was gone was any mention of it in the
 * process, because the card at the bottom of the workflow page still said
 * the inspection was described there and not built. He read the
 * documentation as the product. In the same breath he caught the card's
 * other stale line, that photos cannot be uploaded, which they had been
 * for months through capture="environment" into the job-photos bucket.
 *
 * ─── WHY THAT CARD ROTS AND NO OTHER SCREEN DOES ───────────────────────
 *
 * Every other screen in this app shows what EXISTS. Delete the feature and
 * the screen goes with it, so those screens cannot lie for long. A NEGATIVE
 * CLAIM HAS NOTHING TO GO WITH. Ship the feature and the sentence saying it
 * does not exist keeps rendering, word perfect, to the shop owner who paid
 * for it. It cannot go stale loudly. It rots in silence and then costs a
 * support call about a screen that was working the whole time.
 *
 * ─── SO EVERY GAP CARRIES ITS OWN PROOF ────────────────────────────────
 *
 * A gap is not a sentence here, it is a sentence plus the patterns that
 * would mean it is finished. tools/gap-check.mjs runs those patterns on
 * every `npm run check` and fails naming the gap, the file and the line
 * that closed it. The card cannot outlive the gap it describes, because
 * the commit that closes the gap is the commit that fails the build.
 *
 * TO CLOSE A GAP: delete its entry. That is the whole procedure, and the
 * failing check says so in the words of the person who will read it.
 *
 * TO OPEN ONE: add an entry, with a proof. Never type "not built" into a
 * page by hand. gap-check.mjs sweeps the rendered text of src/pages for
 * those phrases too, so a claim with no proof fails the same build as a
 * proof that has come true.
 *
 * WHAT A PROOF MUST BE. Something the feature cannot exist without, not
 * something its author might have named it. A status is a value in the
 * STATUS array and in a check constraint; a screen is a file and a route.
 * A proof aimed at a file that has since moved proves nothing, so the
 * check asserts every file and directory named here still exists.
 *
 * NOT A PLACE FOR ROADMAP COPY. The sales page carries the roadmap. This
 * carries only the gaps the APP ITSELF points at, and each one is worded
 * as the shop reads it on the screen.
 */

/**
 * A pattern that, if it ever matches, means the gap is closed.
 *
 * `file` is one file; `dir` is every source file beneath it, which is how
 * a gap survives the feature landing in a migration or a route nobody
 * could have named in advance. `within` slices the file first, so a proof
 * about the STATUS array does not fire on the word "parts" somewhere else
 * in the same file.
 */
export type Proof = {
  /** Repo-relative path to one file. Exactly one of `file` or `dir`. */
  file?: string;
  /** Repo-relative directory, swept for .ts, .mjs and .sql. */
  dir?: string;
  /** With `file`: narrow to this slice before looking. */
  within?: RegExp;
  /**
   * With `dir`: the gap is closed by a FILE whose name matches, whatever
   * is inside it. A screen is a file before it is any particular line.
   */
  names?: RegExp;
  /** If this matches, the gap is finished and the claim is now false. */
  closedWhen?: RegExp;
  /** What a match means, printed as the reason the build failed. */
  means: string;
};

export type Screen = "workflow" | "more" | "parts";

export type Gap = {
  key: string;
  /** Which screens render this sentence to the shop. */
  shownOn: Screen[];
  /**
   * The row heading, where the screen shows one. `{Parts}` resolves to the
   * shop's own word for parts, so an entry here stays free of the trade
   * vocabulary the page already knows how to substitute.
   */
  title: string;
  /** The row icon, for a screen that draws rows rather than paragraphs. */
  icon?: string;
  /** The sentence the shop reads. Plain spoken, same as every other card. */
  body: string;
  /**
   * The longer version, for a screen with room for a paragraph rather than
   * a row. ONE gap, two lengths — not two entries, because two entries is
   * how the same claim came to be typed on the process page and the parts
   * screen in different words, and only one of them got corrected.
   */
  detail?: string;
  /**
   * When this was last read against the code, not when it was written.
   * A negative claim with no date is a rumor: nobody can tell whether it
   * was checked this week or in February.
   */
  checked: string;
  /**
   * Shown only to a trade whose records are vehicles. A lock shop being
   * offered a coming-soon promise for a vehicle parts catalog is worse
   * than a gap, because "not built" reads as "not built YET".
   */
  vehiclesOnly?: boolean;
  /**
   * Shown only to a trade that ORDERS its non-labour lines, which is every
   * trade whose vocabulary carries a part number. NYIA's non-labour lines
   * are a vehicle fee per day, database searches and filing fees: nothing
   * is ordered, nothing arrives, and a job is never held up waiting for
   * one. Promising them a waiting-on-parts state is the same mistake
   * vehiclesOnly exists to stop, one shelf over.
   *
   * NOT the same question as vehiclesOnly. A locksmith has no vehicle
   * catalog and still orders real parts and still waits on them.
   */
  orderedPartsOnly?: boolean;
  proof: Proof[];
};

/** The pill on a not-built row. Here so no page types the words by hand. */
export const NOT_BUILT_PILL = "not built";

/** The heading over a card or a group of them. Same reason. */
export const NOT_BUILT_GROUP = "Not built yet";

export const GAPS: Gap[] = [
  {
    key: "waiting-on-parts-status",
    shownOn: ["workflow"],
    title: "A job waiting on a part",
    body: "A job waiting on a part looks exactly like a job being worked on right now. "
      + "That deserves its own state and does not have one yet.",
    checked: "2026-09-18",
    orderedPartsOnly: true,
    proof: [
      {
        /* The seven the app knows. A status the screens do not list is a
           status no board can filter on, so this array is the honest test
           of whether the state exists for anybody using it. */
        file: "src/data/jobs.ts",
        within: /export const STATUS = \[[^\]]*\]/,
        closedWhen: /\b(waiting|awaiting|on_order|parts)\w*/i,
        means: "the STATUS list on the jobs screen now carries a waiting-on-parts state",
      },
      {
        /* And the constraint, because a status can arrive in a migration
           weeks before a screen filters on it. Any later migration that
           rewrites the check is swept the same way. */
        dir: "db/migrations",
        closedWhen: /status in \([^)]*'(waiting\w*|awaiting\w*|\w*parts\w*|on_order)'/i,
        means: "a migration has added a waiting-on-parts value to the job status constraint",
      },
    ],
  },
  {
    key: "parts-catalog-lookup",
    /* Everything Else shows it as a row; the parts screen has room for the
       sentence. Two screens, one entry, so they cannot be corrected apart. */
    shownOn: ["more", "parts"],
    title: "{Parts} lookup",
    icon: "parts",
    body: "Live catalog and ordering",
    detail: "The live catalog lookup. There is a supplier catalog key in the environment and no "
      + "screen behind it, so ordering still happens the way it does now.",
    checked: "2026-09-18",
    /* A live AUTO parts catalog, searched by vehicle. A door company buys
       doors and an investigator buys database searches, and neither is
       ever going to be looked up in it. */
    vehiclesOnly: true,
    proof: [
      {
        dir: "server",
        closedWhen: /partstech/i,
        means: "the server now calls the parts catalog, so the lookup is built",
      },
    ],
  },
  {
    key: "manual-lookups-log",
    shownOn: ["more"],
    title: "Manual lookups",
    icon: "manuals",
    body: "What was looked up, and on what",
    checked: "2026-09-19",
    /**
     * The LOG of what was looked up in Manuals. Manuals is recalls, wiring
     * diagrams and factory portals, every source behind it a vehicle
     * manufacturer, so the shelf and its history are gated together.
     *
     * ─── THIS ONE WAS THE WEAKEST PROOF IN THE FILE, 2026-09-18 ────────
     *
     * It had two patterns and both were GUESSES AT A NAME: a table called
     * manual_lookups or lookup_log, a route mentioning the same. Ship the
     * feature as `vehicle_history` and the check stays quiet, which is a
     * proof that has already failed and cannot tell you so. The rule at
     * the top of this file says a proof must name something the feature
     * cannot exist without, and a name is the one thing it can always be
     * given differently.
     *
     * The sales page says exactly what the feature is: "Manual lookups
     * recorded on the customer file." That is two halves, and each half
     * has a surface today that provably does neither, so each half is a
     * real proof rather than a guess.
     *
     * WHAT MAKES THE FIRST ONE STRONG. public/manuals.html already knows
     * the vehicle AND the customer -- it reads
     * vehicles(customers(full_name), jobs(...)) to build the history it
     * shows. What it had never done was WRITE. A log that records nothing
     * is not a log, so the write is the thing this feature cannot be
     * built without, whatever the table ends up being called.
     *
     * ─── NARROWED 2026-09-19, AND WHY THAT IS NOT WEAKENING IT ────────
     *
     * "insert, upsert and rpc are all zero in that file" stopped being
     * true the day the OEM pass ledger landed: the page now writes an
     * oem_passes row when somebody says they bought a manufacturer day
     * pass. That is a purchase, not a lookup -- it records what the SHOP
     * bought, names no customer and no vehicle, and the "Manual lookups"
     * row in more.ts is still ready:false because nothing logs what was
     * looked up on whose car.
     *
     * So the proof now excludes exactly that one table and nothing else.
     * A write to ANY other table from this page still fires, and so does
     * any rpc. The alternative -- deleting this gap because a detector
     * went off -- would have left the app promising a shop something no
     * proof was watching any more, which is the failure this whole file
     * exists to prevent.
     *
     * AND THE SECOND. "On the customer file" means the customer file
     * shows it. src/pages/customerdrawer.ts reads jobs, profiles and
     * vehicles, and the words lookup and manual do not appear in it at
     * all. Either half landing closes the gap, and whichever lands first
     * fails the build.
     *
     * The two name guesses are kept underneath as a backstop. They cost
     * nothing and they would catch a migration that lands before either
     * screen moves.
     */
    vehiclesOnly: true,
    proof: [
      {
        /* A write from the page that already holds the vehicle and the
           customer means it has started recording what was done with
           them -- EXCEPT to oem_passes, which records what the shop
           bought rather than what was looked up. Any other table, and
           any rpc, still closes this. */
        file: "public/manuals.html",
        closedWhen: /\.from\(['"](?!oem_passes)[^'"]*['"]\)\s*\.(insert|upsert)\(|\.rpc\(/,
        means: "the manuals page now writes something other than a pass purchase, and it "
          + "already holds the vehicle and the customer, so what it is recording is the lookup",
      },
      {
        /* The reading half. The customer file is this screen.

           NO WORD BOUNDARIES, and that is deliberate. The first version of
           this pattern was \b(lookup|lookups)\b and it did not fire on
           `recentLookups`, which is how somebody would really name the
           variable. A boundary either side means the token has to stand
           alone, and in real code it almost never does. "lookup" is
           distinctive enough to carry the match on its own: the word does
           not appear anywhere in this file today. */
        file: "src/pages/customerdrawer.ts",
        closedWhen: /lookup|manuals?_history/i,
        means: "the customer file now shows lookups, which is the whole of what was promised",
      },
      {
        dir: "db/migrations",
        closedWhen: /create table (if not exists )?(public\.)?(manual_lookups|lookup_log|lookups)\b/i,
        means: "a table now records what was looked up, so the log exists",
      },
      {
        dir: "server/routes",
        closedWhen: /manual_lookups|lookup_log\b/i,
        means: "the server now writes a lookup history, so the log exists",
      },
    ],
  },
  {
    /**
     * THE HALF OF A SENTENCE THAT WAS STILL TRUE.
     *
     * The parts screen said "Core deposits and returns are not tracked
     * here." On 2026-09-18 half of that was false, and it was the half
     * California has an opinion about. Business and Professions Code
     * 9884.10 gives a customer the right to their replaced parts and the
     * offer has to be made when they authorize the work: garage.html puts
     * the box in front of them, server/routes/garage.ts stores the answer
     * on the authorization, and server/desk/invoice-email.ts prints it,
     * with src/domain/partsreturn.ts holding the rule and its two
     * exceptions. Returns are tracked. The screen said they were not.
     *
     * What is still true is the MONEY. line_items.is_core and
     * parts.core_charge are both columns no screen writes, so the deposit
     * on an exchange part is not a figure this system holds. invoice-email
     * READS is_core to decide what the return note may promise, which is
     * not the same as the shop being able to set it.
     *
     * Split rather than corrected, because one sentence carrying a true
     * claim and a false one cannot be checked at all.
     */
    key: "core-deposits",
    shownOn: ["parts"],
    title: "Core deposits",
    body: "A core deposit is not a figure this system holds yet. Put it on the line as cost like "
      + "any other charge, and settle the credit with your supplier the way you do now.",
    checked: "2026-09-18",
    proof: [
      {
        /* A deposit the shop can see is a deposit some screen writes. Both
           columns have existed since the first migration and neither has
           ever had a screen behind it. */
        dir: "src/pages",
        closedWhen: /\b(is_core|core_charge|coreCharge)\b/,
        means: "a screen now reads or writes the core flag or the core charge",
      },
    ],
  },
];

/**
 * One gap's sentence, by key, for a step that has to say it in place.
 *
 * Throws on an unknown key rather than returning an empty string, because
 * a step that silently renders nothing is the quiet failure this whole
 * file exists to stop. Deleting a gap has to break the thing that quotes
 * it, loudly, in the same breath.
 */
export const gap = (key: string): string => {
  const g = GAPS.find((x) => x.key === key);
  if (!g) throw new Error(`gaps: no gap named "${key}". If it was just built, the step quoting it `
    + "has to be rewritten rather than left pointing at nothing.");
  return g.body;
};

/** What one screen has to show, in the order it is written above. */
export const gapsOn = (screen: Screen, hasVehicles = true, ordersParts = true): Gap[] =>
  GAPS.filter((g) => g.shownOn.includes(screen)
    && (hasVehicles || !g.vehiclesOnly)
    && (ordersParts || !g.orderedPartsOnly));

/**
 * The date to print under a card, as the shop reads dates.
 *
 * The date goes on the SCREEN and not only in this file, because the
 * reader is the one who can tell it is wrong. Anthony knew the inspection
 * was built; what he could not tell was that the sentence saying it was
 * not had been sitting there since before it shipped.
 */
export const lastChecked = (gaps: Gap[]): string | null => {
  const dates = gaps.map((g) => g.checked).filter(Boolean).sort();
  const oldest = dates[0];
  if (!oldest) return null;
  const d = new Date(`${oldest}T12:00:00Z`);
  return d.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
};
